import React, { useState, useEffect } from 'react';
import { useOptimize } from '../../hooks/useOptimize';
import { ConfirmModal } from '../common/ConfirmModal';
import { OptimizeMatriz } from './OptimizeMatriz';
import { formatCurrencyInt } from '../../utils/formatters';
import { IconDividido, IconUnitario } from '../common/ModoIcons';

/**
 * Campo del valor (total) editable de una herramienta. Muestra la cifra formateada
 * con separador de miles y, al enfocar, el número crudo para editar.
 */
const CostoInput = ({ value, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');

  const shown = editing ? val : (value ? value.toLocaleString('en-US') : '');

  const handleFocus = (e) => {
    const el = e.target;
    setEditing(true);
    setVal(value ? String(value) : '');
    requestAnimationFrame(() => el.select());
  };

  const commit = () => {
    const c = parseFloat(String(val).replace(/[^\d.]/g, '')) || 0;
    setEditing(false);
    if (c !== value) onSave(c);
  };

  return (
    <div className="tbbc-costo-field">
      <span className="tbbc-costo-prefix">$</span>
      <input
        className="tbbc-costo-input"
        type="text"
        inputMode="numeric"
        value={shown}
        placeholder="0"
        draggable={false}
        onFocus={handleFocus}
        onChange={(e) => setVal(e.target.value.replace(/[^\d.]/g, ''))}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
        }}
      />
    </div>
  );
};

/**
 * Control segmentado del modo de cobro de una herramienta:
 *  - unitario: cada cliente marcado paga el valor completo.
 *  - dividido: el valor se reparte entre los clientes marcados.
 */
const ModoToggle = ({ value, onChange }) => {
  const isDiv = value === 'dividido';
  return (
    <div className="tbbc-modo" role="group" aria-label="Modo de cobro">
      <button
        type="button"
        className={`tbbc-modo-opt is-uni ${isDiv ? '' : 'active'}`}
        draggable={false}
        aria-pressed={!isDiv}
        title="Unitario · el valor completo a cada cliente marcado"
        onClick={() => onChange('unitario')}
      >
        <IconUnitario />
      </button>
      <button
        type="button"
        className={`tbbc-modo-opt is-div ${isDiv ? 'active' : ''}`}
        draggable={false}
        aria-pressed={isDiv}
        title="Dividido · reparte el valor entre los clientes marcados"
        onClick={() => onChange('dividido')}
      >
        <IconDividido />
      </button>
    </div>
  );
};

/**
 * Nombre editable de una herramienta. Se ve como texto plano; al enfocar se vuelve
 * un campo. Estado local; persiste al salir (vacío descarta el cambio).
 */
const NombreInput = ({ value, onSave }) => {
  const [val, setVal] = useState(value || '');

  useEffect(() => {
    setVal(value || '');
  }, [value]);

  const commit = () => {
    const n = val.trim();
    if (n && n !== value) onSave(n);
    else setVal(value || '');
  };

  return (
    <input
      className="tbbc-nombre-input"
      value={val}
      placeholder="Nombre"
      draggable={false}
      title="Editar nombre"
      onChange={(e) => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
      }}
    />
  );
};

/**
 * Módulo Optimize (2026-05). Cada herramienta tiene un valor. Se comparte entre los
 * proyectos que la usan y su valor se DIVIDE en partes iguales entre ellos
 * (marcar 1 = costo completo; marcar 5 = 20% c/u). La tabla de abajo suma,
 * por proyecto, la parte que le corresponde de cada herramienta.
 */
export const OptimizeModule = ({ showNotification }) => {
  const {
    proyectos,
    apps,
    asignaciones,
    loadProyectos,
    loadApps,
    loadAsignaciones,
    addProyecto,
    removeProyecto,
    reorderProyectos,
    addApp,
    updateAppCosto,
    updateAppName,
    updateAppModo,
    removeApp,
    reorderApps,
    setAsignacion
  } = useOptimize(showNotification);

  const [nuevoProyecto, setNuevoProyecto] = useState('');
  const [nuevaApp, setNuevaApp] = useState('');
  const [nuevoCosto, setNuevoCosto] = useState('');
  const [confirm, setConfirm] = useState(null);
  const [dragInfo, setDragInfo] = useState(null);
  const [dragOverProyectoId, setDragOverProyectoId] = useState(null);
  const [dragOverAppId, setDragOverAppId] = useState(null);

  useEffect(() => {
    loadProyectos();
    loadApps();
    loadAsignaciones();
  }, [loadProyectos, loadApps, loadAsignaciones]);

  const handleAddProyecto = async () => {
    if (!nuevoProyecto.trim()) return;
    if (await addProyecto(nuevoProyecto.trim())) setNuevoProyecto('');
  };

  const handleAddApp = async () => {
    if (!nuevaApp.trim()) return;
    if (await addApp(nuevaApp.trim(), parseFloat(nuevoCosto) || 0)) {
      setNuevaApp('');
      setNuevoCosto('');
    }
  };

  // --- Drag & drop ---
  const handleProyectoDragStart = (e, p) => {
    setDragInfo({ type: 'proyecto', id: p.id });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', `proyecto:${p.id}`);
  };

  const handleAppDragStart = (e, app) => {
    setDragInfo({ type: 'optimize-app', id: app.id });
    e.dataTransfer.effectAllowed = 'copyMove';
    e.dataTransfer.setData('text/plain', `optimize-app:${app.id}`);
  };

  const handleDragEnd = () => {
    setDragInfo(null);
    setDragOverProyectoId(null);
    setDragOverAppId(null);
  };

  // Reordenar el catálogo: soltar una app sobre otra app
  const handleAppDragOverCard = (e, app) => {
    if (dragInfo?.type !== 'optimize-app') return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverAppId !== app.id) setDragOverAppId(app.id);
  };

  const handleAppDragLeaveCard = (app) => {
    if (dragOverAppId === app.id) setDragOverAppId(null);
  };

  const handleAppDropCard = (e, app) => {
    if (dragInfo?.type !== 'optimize-app') return;
    e.preventDefault();
    e.stopPropagation();
    const fromId = dragInfo.id;
    setDragOverAppId(null);
    setDragInfo(null);
    if (fromId === app.id) return;
    const list = [...apps];
    const from = list.findIndex((x) => x.id === fromId);
    const to = list.findIndex((x) => x.id === app.id);
    if (from === -1 || to === -1) return;
    const [moved] = list.splice(from, 1);
    list.splice(to, 0, moved);
    reorderApps(list);
  };

  const handleProyectoDragOver = (e, p) => {
    if (!dragInfo) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = dragInfo.type === 'proyecto' ? 'move' : 'copy';
    if (dragOverProyectoId !== p.id) setDragOverProyectoId(p.id);
  };

  const handleProyectoDragLeave = (p) => {
    if (dragOverProyectoId === p.id) setDragOverProyectoId(null);
  };

  const handleProyectoDrop = (e, p) => {
    e.preventDefault();
    const info = dragInfo;
    setDragOverProyectoId(null);
    setDragInfo(null);
    if (!info) return;

    if (info.type === 'proyecto') {
      if (info.id === p.id) return;
      const list = [...proyectos];
      const from = list.findIndex((x) => x.id === info.id);
      const to = list.findIndex((x) => x.id === p.id);
      if (from === -1 || to === -1) return;
      const [moved] = list.splice(from, 1);
      list.splice(to, 0, moved);
      reorderProyectos(list);
    } else if (info.type === 'optimize-app') {
      const app = apps.find((a) => a.id === info.id);
      if (!app) return;
      setAsignacion(app.id, p.id, true);
      showNotification?.('success', 'Optimize', `"${app.name}" compartida con "${p.name}"`);
    }
  };

  const pedirEliminarProyecto = (e, p) => {
    e.stopPropagation();
    setConfirm({
      title: 'Eliminar proyecto',
      message: `¿Eliminar el proyecto "${p.name}"? Se quitará de las asignaciones.`,
      onConfirm: async () => {
        await removeProyecto(p);
        setConfirm(null);
      }
    });
  };

  const pedirToggleAsignacion = (appId, clientId, asignado) => {
    const app = apps.find((a) => a.id === appId);
    const proyecto = proyectos.find((p) => p.id === clientId);
    setConfirm({
      title: asignado ? 'Compartir herramienta' : 'Quitar herramienta',
      message: asignado
        ? `¿Compartir "${app?.name}" con "${proyecto?.name}"? El valor se repartirá en partes iguales entre los proyectos marcados.`
        : `¿Quitar "${app?.name}" de "${proyecto?.name}"? El valor se repartirá entre los proyectos que queden.`,
      confirmLabel: asignado ? 'Compartir' : 'Quitar',
      onConfirm: () => {
        setAsignacion(appId, clientId, asignado);
        setConfirm(null);
      }
    });
  };

  const pedirEliminarApp = (app) => {
    setConfirm({
      title: 'Eliminar herramienta',
      message: `¿Eliminar "${app.name}"? Se quitará de todos los proyectos.`,
      onConfirm: async () => {
        await removeApp(app);
        setConfirm(null);
      }
    });
  };

  return (
    <div className="module-proceso">
      <div className="module-header">
        <h1>Optimize</h1>
        <p>El valor de cada herramienta se divide en partes iguales entre los proyectos marcados</p>
      </div>

      <div className="tbbc-grid">
        {/* Columna 1 — Proyectos */}
        <section className="proceso-col">
          <header className="proceso-col-header">
            <span className="proceso-col-num">1</span>
            <span className="proceso-col-title">Proyectos</span>
            <span className="proceso-col-count">{proyectos.length}</span>
          </header>
          <div className="proceso-col-body">
            <div className="proceso-form">
              <input
                className="proceso-input"
                value={nuevoProyecto}
                onChange={(e) => setNuevoProyecto(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddProyecto()}
                placeholder="Nombre del proyecto"
              />
              <button className="btn-primary proceso-add-btn" onClick={handleAddProyecto}>
                Agregar
              </button>
            </div>
            <ul className="proceso-list">
              {proyectos.length === 0 && (
                <li className="proceso-list-empty">Sin proyectos todavía</li>
              )}
              {proyectos.map((p) => {
                const isDragging = dragInfo?.type === 'proyecto' && dragInfo.id === p.id;
                let dragOverClass = '';
                if (dragOverProyectoId === p.id && dragInfo) {
                  dragOverClass =
                    dragInfo.type === 'proyecto' ? 'drag-over-reorder' : 'drag-over-app';
                }
                return (
                  <li
                    key={p.id}
                    className={`proceso-list-item ${isDragging ? 'dragging' : ''} ${dragOverClass}`}
                    draggable
                    onDragStart={(e) => handleProyectoDragStart(e, p)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => handleProyectoDragOver(e, p)}
                    onDragLeave={() => handleProyectoDragLeave(p)}
                    onDrop={(e) => handleProyectoDrop(e, p)}
                  >
                    <span className="proceso-list-name">{p.name}</span>
                    <button
                      className="proceso-del-btn"
                      onClick={(e) => pedirEliminarProyecto(e, p)}
                      title="Eliminar proyecto"
                    >
                      ×
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>

        {/* Columna 2 — Herramientas con costo */}
        <section className="proceso-col">
          <header className="proceso-col-header">
            <span className="proceso-col-num">2</span>
            <span className="proceso-col-title">Herramientas</span>
            <span className="proceso-col-count">{apps.length}</span>
          </header>
          <div className="proceso-col-body">
            <div className="proceso-form">
              <input
                className="proceso-input"
                value={nuevaApp}
                onChange={(e) => setNuevaApp(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddApp()}
                placeholder="Nombre de la herramienta"
              />
              <input
                className="proceso-input tbbc-costo-nuevo"
                type="number"
                min="0"
                step="0.01"
                value={nuevoCosto}
                onChange={(e) => setNuevoCosto(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddApp()}
                placeholder="Costo"
              />
              <button className="btn-primary proceso-add-btn" onClick={handleAddApp}>
                Agregar
              </button>
            </div>
            <p className="proceso-hint">
              Arrastra una herramienta (⠿) a un proyecto para asignarla, o sobre otra para reordenar.
            </p>
            <div className="proceso-list">
              {apps.length === 0 && <div className="proceso-list-empty">Catálogo vacío</div>}
              {apps.map((app) => {
                // Solo cuenta los proyectos marcados que siguen activos (igual
                // que la matriz); evita contar asignaciones de clientes inactivos.
                const count = proyectos.reduce(
                  (n, p) => n + ((asignaciones[app.id] || {})[p.id] ? 1 : 0),
                  0
                );
                const estado = count > 0 ? 'distribuido' : 'pendiente';
                const estadoTexto =
                  count > 0
                    ? `Compartida entre ${count} proyecto${count === 1 ? '' : 's'}`
                    : 'Sin compartir';
                const isDiv = app.modo !== 'unitario';
                const reparteValor = count > 0
                  ? (isDiv ? app.costo / count : app.costo * count)
                  : 0;
                const readoutTitle = count === 0
                  ? 'Sin compartir'
                  : (isDiv
                      ? `${formatCurrencyInt(app.costo)} ÷ ${count} = ${formatCurrencyInt(app.costo / count)} por cliente`
                      : `${formatCurrencyInt(app.costo)} × ${count} = ${formatCurrencyInt(app.costo * count)} en total`);
                const isDragging = dragInfo?.type === 'optimize-app' && dragInfo.id === app.id;
                const isDragOver = dragOverAppId === app.id && dragInfo?.type === 'optimize-app';
                return (
                  <div
                    key={app.id}
                    className={`proceso-app-card tbbc-app-card ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over-reorder' : ''}`}
                    onDragOver={(e) => handleAppDragOverCard(e, app)}
                    onDragLeave={() => handleAppDragLeaveCard(app)}
                    onDrop={(e) => handleAppDropCard(e, app)}
                  >
                    <span
                      className="tbbc-grip"
                      draggable
                      onDragStart={(e) => handleAppDragStart(e, app)}
                      onDragEnd={handleDragEnd}
                      title="Arrastrar para reordenar o asignar a un proyecto"
                    >
                      ⠿
                    </span>
                    <span className={`estado-dot estado-${estado}`} title={estadoTexto} />
                    <NombreInput value={app.name} onSave={(n) => updateAppName(app, n)} />
                    <ModoToggle value={app.modo} onChange={(m) => updateAppModo(app, m)} />
                    <CostoInput value={app.costo} onSave={(c) => updateAppCosto(app, c)} />
                    {count > 0 ? (
                      <span className={`tbbc-divido ${isDiv ? 'is-div' : 'is-uni'}`} title={readoutTitle}>
                        <span className="tbbc-divido-op">{isDiv ? `÷${count}` : `${count}×`}</span>
                        <span className="tbbc-divido-val">{formatCurrencyInt(reparteValor)}</span>
                      </span>
                    ) : (
                      <span className="tbbc-divido tbbc-divido-none" title="Sin asignar">—</span>
                    )}
                    <button
                      className="proceso-del-btn"
                      onClick={() => pedirEliminarApp(app)}
                      title="Eliminar herramienta"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </div>

      <OptimizeMatriz
        proyectos={proyectos}
        apps={apps}
        asignaciones={asignaciones}
        onToggle={pedirToggleAsignacion}
        showNotification={showNotification}
      />

      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          onConfirm={confirm.onConfirm}
          onClose={() => setConfirm(null)}
        />
      )}
    </div>
  );
};
